import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { isMobile } from 'react-device-detect';
import PropTypes from 'prop-types';
import Icon from 'Components/Icon';
import { classnames } from 'Utils/classnames';
import { bodyScrollLock, bodyScrollUnLock } from 'Utils/bodyScroll';

const Drawer = ({
  open,
  onClose,
  children,
  position,
  buttonArea = null,
  type,
  icon,
  title,
  container,
  disableOverlayClick,
  closeIconVisible,
  closeIconName,
  contentClassName,
  lockBackgroundScroll,
  className,
  headerColor,
  modalSize,
  centered = false,
  buttonsReversed = false,
  showCloseOnBottom = false
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const dragHandleRef = useRef(null);
  const drawerContentRef = useRef(null);
  const [pushY, setPushY] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(null);
  const isCentered = centered && !isMobile;

  useEffect(() => {
    if (lockBackgroundScroll && open) {
      bodyScrollLock();
    }

    return () => {
      bodyScrollUnLock();
    };
  }, [open]);

  useEffect(() => {
    if (!open || position !== 'bottom' || typeof window === 'undefined') return;

    const update = () => {
      const vv = window.visualViewport;
      setViewportHeight(vv ? vv.height : window.innerHeight);
    };

    update();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    return () => vv?.removeEventListener('resize', update);
  }, [open, position]);

  const handleCloseClick = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 100);
  };

  const overlayClick = () => {
    if (!disableOverlayClick)
      handleCloseClick();
  };

  const handleDrawerClick = event => {
    event.stopPropagation();
  };

  const tap = event => {
    const { pageY } = event.touches[0];
    event.target.setAttribute('data-startY', pageY);
  };

  const drag = event => {
    const { pageY } = event.touches[0];
    const startY = Number(event.target.getAttribute('data-startY'));
    event.target.setAttribute('data-lastDragY', pageY);
    if (pageY > startY) {
      setPushY(pageY - startY);

    }
  };

  const release  = event => {
    setPushY(0);
    const startY = Number(event.target.getAttribute('data-startY'));
    const lastDragY = Number(event.target.getAttribute('data-lastDragY'));

    if ((lastDragY - startY) > 150) {
      onClose();
    }

  };


  useEffect(() => {
    if (dragHandleRef?.current) {
      dragHandleRef?.current?.addEventListener('touchend', release);
      dragHandleRef?.current?.addEventListener('touchmove', drag);
      dragHandleRef?.current?.addEventListener('touchstart', tap);
    }

    return () => {
      dragHandleRef?.current?.removeEventListener('touchstart', tap);
      dragHandleRef?.current?.removeEventListener('touchmove', drag);
      dragHandleRef?.current?.removeEventListener('touchend', release);
    };
  }, [dragHandleRef, children]);

  const renderFooter = () => {

    const _buttonContainerClassName = classnames({
      'blyn-drawer__content__button-container': true,
      'blyn-drawer__content__button-container--right': position === 'right',
      'blyn-drawer__content__button-container--bottom': position === 'bottom',
      'blyn-drawer__content__button-container--reversed': buttonsReversed,
    });

    if (buttonArea) {
      const { primaryButton, secondaryButton } = buttonArea;

      const _secondaryButtonClassName = classnames({
        btn: true,
        'btn--block': secondaryButton?.block,
        'btn--disabled': secondaryButton?.disabled,
        'btn--outline': !secondaryButton?.className,
        [secondaryButton?.className]: !!secondaryButton?.className,
      });

      const _primaryButtonClassName = classnames({
        btn: true,
        'btn--block': primaryButton?.block,
        'btn--disabled': primaryButton?.disabled,
        'btn--primary': !primaryButton?.className,
        [primaryButton?.className]: !!primaryButton?.className,
      });

      return (
        <div className={ _buttonContainerClassName }>
          { primaryButton && (
            <button
              className={ _primaryButtonClassName }
              disabled={ primaryButton.disabled }
              type="button"
              onClick={ () => {
                primaryButton?.onClick && buttonArea.primaryButton?.onClick();
              } }
            >
              { primaryButton?.label }
            </button>
          ) }
          { secondaryButton && (
            <button
              type="button"
              disabled={ secondaryButton.disabled }
              className={ _secondaryButtonClassName }
              onClick={ () => {
                secondaryButton?.onClick && buttonArea.secondaryButton?.onClick();
              } }
            >
              { secondaryButton?.label }
            </button>
          ) }
        </div>
      );
    }
  };

  const renderIcon = () => {
    if (icon) return <div className='blyn-drawer__header__icon'>{ icon }</div>;
    switch (type) {
      case 'success':
        return <div className='blyn-drawer__header__icon'>{ <Icon iconName="svgThumpsUp" /> }</div>;
      case 'error':
        return <div className='blyn-drawer__header__icon'>{ <Icon iconName="svgAlert" /> }</div>;
      default:
        return null;
    }
  };

  const renderHeader = () => {
    const headerClassNames = classnames({
      'blyn-drawer__header': true,
      'blyn-drawer__header--right': position === 'right',
      'blyn-drawer__header--center': position === 'center',
      'blyn-drawer__header--close-on-bottom': showCloseOnBottom,
      [`blyn-drawer__header--${headerColor}`]: !!headerColor,
    });

    const titleClassName = classnames({
      'blyn-drawer__header__title': true,
      [`blyn-drawer__header__title--${type}`]: !!type,
    });

    return (
      <div className={ headerClassNames }>
        { renderIcon() }
        { title && <div className={ titleClassName } dangerouslySetInnerHTML={ { __html: title } } /> }
        { ((position != 'bottom' && position != 'top') || showCloseOnBottom) && closeIconVisible && (
          <Icon
            className='blyn-drawer__header__close-icon'
            iconName={
              closeIconName
                || ((isCentered || showCloseOnBottom) ? 'svgModalCloseX' : 'svgCloseBlack')
            }
            onClick={ () => {
              handleCloseClick();
            } }
          />
        ) }
      </div>
    );
  };

  const getDirectionStyle = ( position, isClosing) => {
    if (isCentered) return {};
    switch (position) {
      case 'right':
        return {
          top: 0,
          bottom: 0,
          right: 0,
          animationName: isClosing ? 'slideOutFromRight' : 'slideInFromRight',
          borderTopLeftRadius: '28px',
          borderBottomLeftRadius: '28px',
          width: '375px'
        };
      case 'bottom':
        return {
          left: 0,
          bottom: 0,
          animationName: isClosing ? 'slideOutFromBottom' : 'slideInFromBottom',
          borderTopRightRadius: '28px',
          borderTopLeftRadius: '28px',
          width: '100%',
          maxHeight: '95%',
        };

      default:
        return {};
    }
  };

  const _contentInnerClassName = classnames({
    'blyn-drawer__content-inner': true,
    [contentClassName]: !!contentClassName,
    'blyn-drawer__content-inner--right': position === 'right',
    'blyn-drawer__content-inner--left': position === 'left',
  });

  const _contentClassName = classnames({
    'blyn-drawer__content': true,
    [`size-${modalSize}`]: isCentered,
    'blyn-drawer__content--center': position === 'center' && !isCentered,
    'blyn-drawer__content--right': position === 'right' && !isCentered,
    'blyn-drawer__content--left': position === 'left' && !isCentered,
  });

  const blynClassName = classnames({
    'blyn-drawer': true,
    centered: isCentered,
    [className]: !!className
  });

  const overlayStyle = position === 'bottom' && viewportHeight
    ? { height: viewportHeight, top: 'auto', bottom: 0, alignItems: 'flex-end' }
    : undefined;

  return open && typeof document !== 'undefined'
    ? createPortal(
      <div className={ blynClassName } style={ overlayStyle } onClick={ overlayClick }>
        <div className={ _contentClassName } ref={ drawerContentRef } style={ {
          ...getDirectionStyle(position, isClosing),
          transform: `translate3d(0, ${pushY}px, 0)`
        } } onClick={ handleDrawerClick }>
          {
            position === 'bottom' && <div className='blyn-drawer__content__drag-handle' ref={ dragHandleRef }>
              <div className='blyn-drawer__content__drag-handle__container' />
            </div>
          }
          { renderHeader() }
          { children && (
            <div className={ _contentInnerClassName }>
              { children }
            </div>
          ) }
          { renderFooter() }
        </div>
      </div>,
      container ? container : document.body
    )
    : null;
};


Drawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node,
  position: PropTypes.oneOfType(PropTypes.string, PropTypes.object),
  buttonArea: PropTypes.any,
  type: PropTypes.oneOf(['none', 'success', 'error', 'warning', 'info']),
  icon: PropTypes.node,
  title: PropTypes.node,
  container: PropTypes.node,
  isCancelButtonVisible: PropTypes.bool,
  disableOverlayClick: PropTypes.bool,
  closeIconVisible: PropTypes.bool,
  closeIconName: PropTypes.string,
  contentClassName: PropTypes.string,
  className: PropTypes.string,
  lockBackgroundScroll: PropTypes.bool,
  headerColor: PropTypes.string,
  centered: PropTypes.bool,
  modalSize: PropTypes.oneOf(['normal', 'large']),
  buttonsReversed: PropTypes.bool,
  showCloseOnBottom: PropTypes.bool,
};

Drawer.defaultProps = {
  position: isMobile ? 'bottom' : 'right',
  isCancelButtonVisible: true,
  disableOverlayClick: false,
  closeIconVisible: true,
  lockBackgroundScroll: true,
};

export default Drawer;
